/**
 * Cliente HTTP único de la aplicación.
 *
 * La sesión viaja en una cookie httpOnly, por lo que aquí no hay ningún token
 * que guardar: sólo hace falta `credentials: 'include'` y reenviar el token
 * CSRF en las mutaciones. Centralizarlo evita que cada pantalla reinvente el
 * manejo de errores y de sesión expirada.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details?: Record<string, string[]>,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Errores que la interfaz debe mostrar junto al campo que los provocó. */
  get fieldErrors(): Record<string, string> {
    const output: Record<string, string> = {};
    for (const [field, messages] of Object.entries(this.details ?? {})) {
      if (messages?.[0]) output[field] = messages[0];
    }
    return output;
  }
}

const CSRF_COOKIE = 'bp_csrf';
const CSRF_HEADER = 'x-csrf-token';
const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export function readCsrfToken(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

type Listener = () => void;
const sessionExpiredListeners = new Set<Listener>();

/** Permite que la capa de autenticación reaccione a un 401 desde cualquier lugar. */
export function onSessionExpired(listener: Listener) {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** No notificar expiración de sesión: lo usa el propio login. */
  silent401?: boolean;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (MUTATING.has(method)) {
    const csrf = readCsrfToken();
    if (csrf) headers[CSRF_HEADER] = csrf;
  }

  const response = await fetch(`/api${path}`, {
    method,
    headers,
    credentials: 'include',
    signal: options.signal,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (response.status === 401 && !options.silent401) {
    sessionExpiredListeners.forEach((listener) => listener());
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload?.error ?? `Error ${response.status}`,
      payload?.details,
      payload?.requestId,
    );
  }

  return payload as T;
}

/**
 * Subida de un archivo.
 *
 * Va aparte de `apiRequest` porque el cuerpo es `FormData`: el navegador tiene
 * que poner él mismo el `Content-Type` con el `boundary`, así que aquí no se
 * fija ninguna cabecera de tipo. El resto —cookie, CSRF, traducción del
 * error— es igual.
 */
export async function apiUpload<T>(path: string, file: File, field = 'file'): Promise<T> {
  const cuerpo = new FormData();
  cuerpo.append(field, file);

  const headers: Record<string, string> = { Accept: 'application/json' };
  const csrf = readCsrfToken();
  if (csrf) headers[CSRF_HEADER] = csrf;

  const response = await fetch(`/api${path}`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: cuerpo,
  });

  if (response.status === 401) sessionExpiredListeners.forEach((listener) => listener());

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload?.error ?? `Error ${response.status}`,
      payload?.details,
      payload?.requestId,
    );
  }

  return payload as T;
}

/**
 * Descarga de un archivo generado por el API (las exportaciones CSV).
 *
 * Un enlace directo no serviría para mostrar un error legible; se pide con la
 * misma cookie, se arma un `Blob` y se descarga con el nombre que da el
 * servidor en `Content-Disposition`.
 */
export async function apiDownload(path: string, fallbackName: string) {
  const response = await fetch(`/api${path}`, { credentials: 'include' });
  if (response.status === 401) sessionExpiredListeners.forEach((listener) => listener());
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new ApiError(response.status, payload?.error ?? `Error ${response.status}`, payload?.details, payload?.requestId);
  }
  const name = /filename="([^"]+)"/.exec(response.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, file: File, field?: string) => apiUpload<T>(path, file, field),
  download: (path: string, fallbackName: string) => apiDownload(path, fallbackName),
};
